import React from "react";
import { useChartTextColor } from "../hooks/useChartTextColor";
import CardLoading from "./CardLoading";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import type { ScoreSummaryItem } from "../types";

interface FlaggedBreakdownChartProps {
  data: ScoreSummaryItem[] | undefined;
  isLoading: boolean;
  isCanadian: boolean;
  onExpand?: () => void;
}

const FlaggedBreakdownChart: React.FC<FlaggedBreakdownChartProps> = ({
  data,
  isLoading,
  isCanadian,
  onExpand,
}) => {
  const textColor = useChartTextColor();

  if (isLoading) {
    return (
      <div className="card h-100">
        <div className="card-header" style={{ backgroundColor: "var(--navbar-and-treeview-bg)", color: "white" }}>
          <span className="panel-title">Flagged by Source</span>
        </div>
        <div className="card-body">
          <CardLoading />
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="card h-100">
        <div className="card-header" style={{ backgroundColor: "var(--navbar-and-treeview-bg)", color: "white" }}>
          <span className="panel-title">Flagged by Source</span>
        </div>
        <div className="card-body">
          <p className="text-muted">No data available</p>
        </div>
      </div>
    );
  }

  // Category display name mapping (backend returns "ATT", "TMobile", etc.)
  const CATEGORY_DISPLAY: Record<string, string> = {
    ATT: isCanadian ? "Rogers/Bell" : "AT&T",
    "Rogers/Bell": "Rogers/Bell",
    TMobile: "T-Mobile",
    Verizon: "Verizon",
    Nomorobo: "Nomorobo",
    RoboKiller: "RoboKiller",
  };

  // MVC hardcodes this category order in categoryAxis.categories
  const CATEGORY_ORDER = [
    isCanadian ? "Rogers/Bell" : "AT&T",
    "T-Mobile",
    "Verizon",
    "Nomorobo",
    "RoboKiller",
  ];

  // Filter out AppStatus (combined app count shown in cards, not chart) — matches MVC
  const mappedData = data
    .filter((item) => item.category !== "AppStatus")
    .map((item) => ({
      ...item,
      category: CATEGORY_DISPLAY[item.category] ?? item.category,
      color: item.color || "var(--primary-btn-color)",
    }));

  // Enforce MVC category order
  const chartData = CATEGORY_ORDER
    .map((cat) => mappedData.find((d) => d.category === cat))
    .filter((d): d is NonNullable<typeof d> => d != null);

  // Dynamically scale the x-axis to the data with "nice" round ticks, instead of
  // fixed 50-increments (which left small datasets stuck at 0–50).
  const maxValue = Math.max(...chartData.map((d) => d.value), 0);
  const { xAxisMax, xTicks } = ((): { xAxisMax: number; xTicks: number[] } => {
    if (maxValue <= 0) return { xAxisMax: 1, xTicks: [0, 1] };
    const roughStep = maxValue / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const residual = roughStep / magnitude;
    const step =
      residual <= 1.5
        ? magnitude
        : residual <= 3
          ? 2 * magnitude
          : residual <= 7
            ? 5 * magnitude
            : 10 * magnitude;
    const niceMax = Math.ceil(maxValue / step) * step;
    return {
      xAxisMax: niceMax,
      xTicks: Array.from({ length: niceMax / step + 1 }, (_, i) => i * step),
    };
  })();

  return (
    <div className="card h-100">
      <div className="card-header" style={{ backgroundColor: "var(--navbar-and-treeview-bg)", color: "white" }}>
        <span className="panel-title">Flagged by Source</span>
      </div>
      <div className="card-body" style={{ height: 459, position: "relative" }}>
        {/* layout="vertical" makes it a horizontal bar chart (categories on Y-axis) matching MVC */}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
            {/* horizontal={false} matches MVC categoryAxis.majorGridLines.visible: false */}
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" ticks={xTicks} domain={[0, xAxisMax]} tick={{ fontSize: 11, fill: textColor }} />
            <YAxis type="category" dataKey="category" tick={{ fontSize: 11, fill: textColor }} width={80} />
            <Tooltip
              cursor={false}
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const item = payload[0].payload;
                return (
                  <div style={{ background: "#fff", color: "#333", border: "1px solid #ccc", padding: "6px 10px", fontSize: 12 }}>
                    {item.category} - {item.percentage}% ({item.value} records)
                  </div>
                );
              }}
            />
            <Bar
              dataKey="value"
              name="Flagged"
              barSize={30}
              activeBar={{ style: { filter: "brightness(1.15)" } }}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {onExpand && (
          <button type="button" onClick={onExpand} style={{ position: "absolute", bottom: 8, left: 8, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
            <img src="/images/dashboard/expand-icon.svg" className="expand" alt="expand" />
          </button>
        )}
      </div>
    </div>
  );
};

export default FlaggedBreakdownChart;
